import isEqual from 'lodash/isEqual';
import uniqueId from 'lodash/uniqueId';
import {
  AlertLevelCode,
  AlertType,
  ProactivityComparisonCode,
  ProactivityType,
  StateCode,
} from 'pleinchamp-api-client';
import React, { ChangeEvent, ComponentPropsWithRef, FC, forwardRef, useEffect, useMemo, useState } from 'react';
import { UnderlinedButton } from '@components/buttons/FlatButton/FlatButton.component';
import { BellIcon } from '@components/icons/Bell.icon';
import { PlcMessage } from '@components/PlcMessage/PlcMessage.component';
import { Text } from '@components/Text/Text.component';
import { TextProps } from '@components/Text/Text.types';
import { Value } from '@form/business/form.types';
import { useWarnIfUnsavedChanges } from '@form/business/UnsavedChange.hook';
import { Checkbox } from '@form/components/Checkbox/Checkbox.component';
import { InlineInput } from '@form/components/Input/Input.component';
import { Select } from '@form/components/Select/Select.component';
import { WarningIcon } from '@icons/Warning.icon';
import { useTranslation } from '@locales/useTranslation.hook';
import { City } from '@meteo/types';
import { NEW_NOTIF_ID } from '@notifs/components/NotificationList/NotificationList.component';
import { AlertState, NotificationType, PlcNotification, ProactivityState } from '@notifs/types';
import { Breakpoint, useIsBreakpointDown } from '@styles/breakpoints';
import { formatNumberWithComma } from '@utils/number';
import classnames from 'classnames';

import './NotificationForm.scss';

interface Props {
  favoriteLocalities: City[];
  initialState: Partial<PlcNotification>;
  isError?: boolean;
  onDelete: (notification: Partial<PlcNotification>) => void;
  onSave: (state: PlcNotification) => void;
}
type FormFragmentProps<M extends PlcNotification> = {
  event: M['type'];
  onChange: (newValues: Partial<M>, isFull: boolean) => void;
  prefixId: string;
  textVariant: TextProps['variant'];
};

function buildInitialRestOfForm(initialState: Props['initialState']): Partial<PlcNotification> {
  if (initialState.notificationType) {
    switch (initialState.notificationType) {
      case NotificationType.NotifProactivity: {
        return {
          comparisonCode: initialState.comparisonCode,
          days: initialState.days,
          threshold: initialState.threshold,
        };
      }
      case NotificationType.NotifAlert: {
        return { levelCode: initialState.levelCode };
      }
      default: {
        const swtichTest: never = initialState.notificationType;
        return swtichTest;
      }
    }
  }
  return {};
}

const NotificationForm: FC<Props & ComponentPropsWithRef<'li'>> = forwardRef(
  ({ favoriteLocalities, initialState, isError, onDelete, onSave, className, ...rest }, formRef) => {
    const { t } = useTranslation(['notifications']);
    const initialRestOfForm = buildInitialRestOfForm(initialState);
    const [stateCode, setStateCode] = useState(initialState.stateCode);
    const [notificationType, setNotificationType] = useState(initialState.notificationType);
    const [locality, setLocality] = useState<City>(initialState.locality || favoriteLocalities[0]);
    const [event, setEvent] = useState(initialState.type);
    const [restOfForm, setRestOfForm] = useState(initialRestOfForm);
    const [isRestOfFormFull, setIsRestOfFormFull] = useState(false);
    const [isFormFull, setIsFormFull] = useState(false);
    const [isFormEdited, setIsFormEdited] = useState(false);
    const [prefixId] = useState(uniqueId('notif-'));
    const isMobile = useIsBreakpointDown(Breakpoint.s);
    const textVariant = isMobile ? 'small' : 'p';
    const isNew = initialState.id === NEW_NOTIF_ID;

    useEffect(() => {
      setIsFormFull(!!(notificationType && event && isRestOfFormFull));
    }, [notificationType, event, isRestOfFormFull, stateCode]);

    useEffect(() => {
      if (isFormFull && stateCode === undefined) {
        setStateCode(StateCode.Enabled);
      }
    }, [isFormFull, stateCode]);

    useEffect(() => {
      const isFormEqualToProps =
        isEqual(stateCode, initialState.stateCode) &&
        isEqual(notificationType, initialState.notificationType) &&
        isEqual(locality, initialState.locality) &&
        isEqual(event, initialState.type) &&
        isEqual(restOfForm, initialRestOfForm);
      if (!isFormEqualToProps && !isFormEdited) {
        setIsFormEdited(true);
      } else if (isFormEqualToProps && isFormEdited) {
        setIsFormEdited(false);
      }
    }, [stateCode, notificationType, locality, event, restOfForm, isFormEdited, initialState]);

    useWarnIfUnsavedChanges(isFormEdited);

    function buildLocalityOptions(localities: City[]) {
      return localities.map(l => ({ label: l.properties.name, value: l.properties.citycode }));
    }

    function buildNotificationTypeOptions(): Value[] {
      return Object.values(NotificationType).map(nt => ({
        label: t(`notifications.form.${nt}.label`),
        value: nt,
      }));
    }

    function handleChangeCityName(value: Value | undefined) {
      if (value) {
        const newLocality = favoriteLocalities.find(l => l.properties.citycode === value.value);
        if (newLocality) {
          setLocality(newLocality);
        }
      }
    }

    function buildEventOptions(type: NotificationType | undefined): Value[] {
      const options: Value[] = [];
      if (type) {
        switch (type) {
          case NotificationType.NotifProactivity: {
            Object.values(ProactivityType).forEach(e =>
              options.push({
                label: t(`notifications.form.event.proactivity.${e}`),
                value: e,
              })
            );
            break;
          }
          case NotificationType.NotifAlert: {
            Object.values(AlertType).forEach(e =>
              options.push({
                label: t(`notifications.form.event.alert.${e}`),
                value: e,
              })
            );
            break;
          }
          default: {
            const switchTest: never = type;
            return switchTest;
          }
        }
      }
      return options;
    }

    function handleChangeNotificationType(value: Value | undefined) {
      if (value) {
        const newType = value.value as NotificationType;
        setNotificationType(newType);
        setEvent(undefined);
      }
    }

    function handleChangeEvent(value: Value | undefined) {
      if (value) {
        const newEvent = value.value as ProactivityType | AlertType;
        setEvent(newEvent);
      }
    }

    function handleStateCode(value: boolean) {
      if (value) {
        setStateCode(StateCode.Enabled);
      } else {
        setStateCode(StateCode.Disabled);
      }
    }

    function onRestChange(newRest: Partial<PlcNotification>, isRestFull: boolean) {
      setRestOfForm(newRest);
      setIsRestOfFormFull(isRestFull);
    }

    function handleSave() {
      const newState = {
        ...initialState,
        ...restOfForm,
        locality,
        notificationType,
        stateCode,
        type: event,
      } as PlcNotification;
      onSave(newState);
    }

    const rootClass = classnames(
      'notification-form',
      className,
      stateCode === StateCode.Disabled ? 'not-active' : '',
      isError && 'error'
    );
    return (
      <>
        <li ref={formRef} className={rootClass} {...rest}>
          <Text className="notification-form-title" tag="div" variant="h5">
            {notificationType === NotificationType.NotifAlert ? (
              <>
                <WarningIcon height="24" width="24" /> <div>{t('notifications.form.alert.label')}</div>
              </>
            ) : (
              <>
                <BellIcon height="24" width="24" /> <div>{t('notifications.form.proactivity.label')}</div>
              </>
            )}
          </Text>
          <form>
            <div className="notification-form-text">
              <Text i18nKey="notifications.form.1" tag="span" variant={textVariant} />
              <span>
                <Select
                  name={`${prefixId}-city`}
                  onChange={handleChangeCityName}
                  options={buildLocalityOptions(favoriteLocalities)}
                  value={{ label: locality.properties.name, value: locality.properties.citycode }}
                  variant={textVariant}
                />
                <span>,</span>
              </span>
              <Text i18nKey="notifications.form.2" tag="span" variant={textVariant} />
              <Select
                disabled={!isNew}
                name={`${prefixId}-type`}
                onChange={handleChangeNotificationType}
                options={buildNotificationTypeOptions()}
                placeholder={t('notifications.form.type.placeholder')}
                selectClassName="notification-type-select"
                value={
                  notificationType
                    ? { label: notificationType.toString(), value: notificationType.toString() }
                    : undefined
                }
                variant={textVariant}
              />
              <Text i18nKey="notifications.form.3" tag="span" variant={textVariant} />
              <Select
                name={`${prefixId}-event`}
                onChange={handleChangeEvent}
                options={buildEventOptions(notificationType)}
                placeholder={t('notifications.form.event.placeholder')}
                value={event ? { label: event.toString(), value: event.toString() } : undefined}
                variant={textVariant}
              />
              {notificationType === NotificationType.NotifProactivity && event && (
                <ProactivityFormFragment
                  event={event as ProactivityType}
                  {...(initialState as ProactivityState)}
                  onChange={onRestChange}
                  prefixId={prefixId}
                  textVariant={textVariant}
                />
              )}
              {notificationType === NotificationType.NotifAlert && event && (
                <AlertFormFragment
                  event={event as AlertType}
                  {...(initialState as AlertState)}
                  onChange={onRestChange}
                  prefixId={prefixId}
                  textVariant={textVariant}
                />
              )}
              {!event && (
                <Text tag="span" variant={textVariant}>
                  .
                </Text>
              )}
            </div>
            <div className="notification-form-bottom-row">
              <Checkbox
                checked={stateCode === StateCode.Enabled}
                disabled={!isFormFull}
                label={t('notifications.form.activated')}
                onCheck={handleStateCode}
              />
              <div className="notification-form-buttons">
                <UnderlinedButton
                  onClick={() => onDelete(initialState)}
                  textProps={{ i18nKey: 'common.delete', variant: textVariant }}
                />
                <UnderlinedButton
                  disabled={!isFormFull || !isFormEdited}
                  onClick={handleSave}
                  textProps={{ flavour: 'red', i18nKey: 'common.save', variant: textVariant }}
                />
              </div>
            </div>
          </form>
        </li>
        {isError && <PlcMessage className="plc-mt-s" i18nKey="notifications.form.error" />}
      </>
    );
  }
);

function getThresholdBoundaries(
  event?: ProactivityType,
  comparisonCode?: ProactivityComparisonCode
): { min: number; max: number } {
  if (event && comparisonCode) {
    switch (event) {
      case ProactivityType.Precipitation: {
        switch (comparisonCode) {
          case ProactivityComparisonCode.Greater: {
            return { max: 100, min: 1 };
          }
          case ProactivityComparisonCode.Lower: {
            return { max: 50, min: 0 };
          }
          default: {
            const switchTestComparison: never = comparisonCode;
            return switchTestComparison;
          }
        }
      }
      case ProactivityType.WindGust: {
        switch (comparisonCode) {
          case ProactivityComparisonCode.Greater: {
            return { max: 150, min: 15 };
          }
          case ProactivityComparisonCode.Lower: {
            return { max: 50, min: 10 };
          }
          default: {
            const switchTestComparison: never = comparisonCode;
            return switchTestComparison;
          }
        }
      }
      case ProactivityType.Temperature: {
        switch (comparisonCode) {
          case ProactivityComparisonCode.Greater: {
            return { max: 45, min: 5 };
          }
          case ProactivityComparisonCode.Lower: {
            return { max: 15, min: -30 };
          }
          default: {
            const switchTestComparison: never = comparisonCode;
            return switchTestComparison;
          }
        }
      }
      default: {
        const switchTest: never = event;
        return switchTest;
      }
    }
  }
  return { max: 0, min: 0 };
}

const ProactivityFormFragment: FC<ProactivityState & FormFragmentProps<ProactivityState>> = fragmentProps => {
  const { t } = useTranslation(['notifications']);
  const { onChange, event, prefixId, textVariant } = fragmentProps;
  const [comparisonCode, setComparisonCode] = useState(
    fragmentProps.comparisonCode || ProactivityComparisonCode.Greater
  );
  const { min, max } = getThresholdBoundaries(event, comparisonCode);
  const [threshold, setThreshold] = useState(fragmentProps.threshold === undefined ? min : fragmentProps.threshold);
  const [days, setDays] = useState(fragmentProps.days || 1);
  const [isError, setIsError] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  function handleChangeComparisonCode(value: Value | undefined) {
    if (value) {
      const newOperator = value.value as ProactivityComparisonCode;
      setComparisonCode(newOperator);
    }
  }

  function handleChangeThreshold(e: ChangeEvent<HTMLInputElement>) {
    const newThreshold = Number(e.target.value);
    setThreshold(newThreshold);
  }

  function handleChangeDays(value: Value | undefined) {
    if (value) {
      const newDays = Number(value.value);
      setDays(newDays);
    }
  }

  useEffect(() => {
    const isFull = !isError && comparisonCode !== undefined && days !== undefined;
    onChange({ comparisonCode, days, threshold }, isFull);
  }, [isError, comparisonCode, threshold, days]);

  useEffect(() => {
    if (!isFirstLoad) {
      const { min: newMin, max: newMax } = getThresholdBoundaries(event, comparisonCode);
      setThreshold(comparisonCode === ProactivityComparisonCode.Greater ? newMin : newMax);
    } else {
      setIsFirstLoad(false);
    }
  }, [event, comparisonCode]);

  useEffect(() => {
    if (threshold >= min && threshold <= max) {
      setIsError(false);
    } else {
      setIsError(true);
    }
  }, [threshold, min, max]);

  function buildComparisonCodeOptions(): Value[] {
    return Object.values(ProactivityComparisonCode).map(o => ({
      label: t(`notifications.form.${o}`),
      value: o,
    }));
  }

  function buildThresholdUnity(evt: AlertType | ProactivityType): string {
    switch (evt) {
      case ProactivityType.Precipitation: {
        return t('meteo.mm');
      }
      case ProactivityType.Temperature: {
        return t('meteo.celcius-long');
      }
      case ProactivityType.WindGust: {
        return t('meteo.kmh');
      }
      default: {
        return '';
      }
    }
  }

  function buildDaysOptions(): Value[] {
    return [1, 2, 3].map(d => ({
      label: t(`notifications.form.days-before`, { count: d }),
      value: d.toString(),
    }));
  }

  const comparisionCodeOptions = buildComparisonCodeOptions();
  const daysOptions = buildDaysOptions();

  return (
    <>
      <Select
        name={`${prefixId}-operator`}
        onChange={handleChangeComparisonCode}
        options={comparisionCodeOptions}
        placeholder={comparisionCodeOptions[0].label}
        value={comparisonCode ? { label: comparisonCode.toString(), value: comparisonCode.toString() } : undefined}
        variant={textVariant}
      />
      <InlineInput
        className={isError ? 'error' : ''}
        max={max}
        min={min}
        onChange={handleChangeThreshold}
        step={1}
        type="number"
        value={threshold.toString()}
        variant={textVariant}
      />
      <Text tag="span" variant={textVariant}>
        {`${buildThresholdUnity(event)},`}
      </Text>
      <Select
        name={`${prefixId}-days`}
        onChange={handleChangeDays}
        options={daysOptions}
        placeholder={daysOptions[0].label}
        value={days ? { label: days.toString(), value: days.toString() } : undefined}
        variant={textVariant}
      />
      <Text tag="span" variant={textVariant}>
        {t('notifications.form.4')}
      </Text>
      {isError && event && (
        <>
          <Text className="notification-form-legend" flavour="red" tag="div" variant="small">
            {t(`common.form.value-between.with-unit`, {
              max: formatNumberWithComma(max, 1),
              min: formatNumberWithComma(min, 1),
              unity: buildThresholdUnity(event),
            })}
          </Text>
        </>
      )}
    </>
  );
};

const AlertFormFragment: FC<AlertState & FormFragmentProps<AlertState>> = fragmentProps => {
  const { t } = useTranslation(['notifications']);
  const { event, onChange, prefixId, textVariant } = fragmentProps;

  function getLevelCodeAsValue(lc: AlertLevelCode) {
    return {
      label: t(`notifications.form.alert-level.${lc}`),
      value: lc,
    };
  }

  function buildLevelCodeOptions(): Value[] {
    return Object.values(AlertLevelCode).map(getLevelCodeAsValue);
  }

  const levelCodeOptions = useMemo(() => buildLevelCodeOptions(), []);

  const [levelCode, setLevelCode] = useState(fragmentProps.levelCode || levelCodeOptions[0].value);

  useEffect(() => {
    onChange({ levelCode }, levelCode !== undefined);
  }, [levelCode]);

  function handleChangeLevelCode(value: Value | undefined) {
    if (value) {
      const newCode = value.value as AlertLevelCode;
      setLevelCode(newCode);
    }
  }

  return (
    <>
      <Text i18nKey="notifications.form.alert-level.label" tag="span" variant={textVariant} />
      <Select
        name={`${prefixId}-levelCode`}
        onChange={handleChangeLevelCode}
        options={levelCodeOptions}
        placeholder={levelCodeOptions[0].label}
        value={getLevelCodeAsValue(levelCode)}
      />
      <Text tag="span" variant={textVariant}>
        .
      </Text>
      {levelCode && event && (
        <>
          <Text className="notification-form-legend with-wrap" flavour="grey" tag="div" variant="tiny">
            {t(`notifications.form.alert.${event}.${levelCode}`)}
          </Text>
          <Text
            className="with-wrap"
            flavour="grey"
            i18nKey="notifications.form.alert.disclaimer"
            tag="div"
            variant="tiny"
          />
        </>
      )}
    </>
  );
};

export { NotificationForm };
